import { XIcon } from "@heroicons/react/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import { HttpError, useOne } from "@refinedev/core";
import Button from "components/button";
import { FullScreenLoading } from "components/fullScreenLoading";
import Input from "components/input";
import { Label } from "components/label";
import { IModalProps, Modal } from "components/modal";
import RadioButton from "components/radioButton";
import { ISeatLayoutData, ISeatLayoutForm, SeatLayoutPosition, SeatLayoutStatus } from "interfaces";
import { get, isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { twMerge } from "tailwind-merge";
import { SEAT_POSITION, SEAT_ROW_NAME_TYPES, SEAT_STATUS, WALKWAY_VALUE } from "utills/constants";
import { z } from "zod";
import { SeatmapTable } from "../seatLayoutTable";
import Select from "components/select";

interface SeatLayoutModalProps extends IModalProps {
  id?: string;
  onSubmit: (data: ISeatLayoutForm) => void;
}

const SeatLayoutSchema = () => {
  return z.object({
    layout_name: z.string().min(1, "This field is required"),
    number_of_columns: z.number({
      invalid_type_error: "Number of columns must be a number",
      required_error: "Number of columns is required",
    }),
    number_of_rows: z.number({
      invalid_type_error: "Number of rows must be a number",
      required_error: "Number of rows is required",
    }),
    row_name_type: z.string().min(1, "This field is required"),
    row_name_position: z.string().min(1, "This field is required"),
    seats: z.array(z.object({
      row_index: z.number({
        invalid_type_error: "Row index must be a number",
        required_error: "Row index is required",
      }),
      column_index: z.number({
        invalid_type_error: "Column index must be a number",
        required_error: "Column index is required",
      }),
      status: z.string().min(1, "This field is required"),
      walkway: z.array(z.string())
    })).min(1, "Seat layout is required")
  }); 
}

export const SeatLayoutModal = ({
  open,
  onClose,
  id,
  onSubmit,
}: SeatLayoutModalProps) => {
  const [isGenerateNewLayout, setIsGenerateNewLayout] = useState(false);
  const { isFetching: isFetchingLayoutDetail } = useOne<ISeatLayoutData, HttpError>({
    resource: "content/seat-maps/layout-seat",
    id: id ?? "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(id && open),
    } as any,
    successNotification: (data, values, resource) => {
      const responseData = get(data, "data", null);
      reset({
        row_name_type: responseData?.row_name_type ?? "alphabet",
        row_name_position: responseData?.row_name_position ?? "left_top_down",
        layout_name: responseData?.layout_name ?? "",
        number_of_columns: responseData?.number_of_columns ?? 1,
        number_of_rows: responseData?.number_of_rows ?? 1,
        seats: responseData?.seats ?? [],  
      })
      return {
        message: `Successfully fetched data from ${resource}.`,
        description: "Success with no errors",
        type: "success",
      };
    }
  });
  
  const {
    register,
    handleSubmit,
    watch,
    control,
    getValues,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ISeatLayoutForm, HttpError, ISeatLayoutForm>({
    defaultValues: {
      row_name_type: "alphabet",
      row_name_position: "left_top_down",
      layout_name: "",
      number_of_columns: 1,
      number_of_rows: 1,
      seats: [],
    },
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        SeatLayoutSchema()
      );
      return createResolver(values, context, options);
    },
  });

  const { fields, replace, update } = useFieldArray({
    control,
    name: "seats"
  });

  const postionSelected = watch("row_name_position");

  // Generate table data based on row and column count
  const generateTableData = (rows: number, cols: number) => {
    if (watch("number_of_rows") < 1 || watch("number_of_columns") < 1) return;
    const existingCellsMap: any = {};
    getValues("seats").forEach(cell => {
      const key = `${cell.row_index}-${cell.column_index}`;
      existingCellsMap[key] = cell.status;
    });

    const newData: any = [];
    
    for (let c = 0; c < cols; c++) {
      for (let r = 0; r < rows; r++) {
        const key = `${r}-${c}`;
        newData.push({
          column_index: c,
          row_index: r,
          status: existingCellsMap[key] || SeatLayoutStatus.AVAILABLE,
          walkway: []
        });
      }
    }
    replace(newData);
  };

  const _updateSeatStatus = (status: SeatLayoutStatus, id: string) => {
    const selectedSeatIndex = fields.findIndex(seat => seat.id === id);
    const position = watch("row_name_position");

    // if (status === SeatLayoutStatus.REMOVE) {
    //   const updated = fields?.filter(seat => seat.id !== id);
    //   setValue("seats", updated);

    //   return;
    // }

    if (
      selectedSeatIndex > -1 &&
      (position === SeatLayoutPosition.LEFT_TOP_DOWN || position === SeatLayoutPosition.LEFT_BOTTOM_UP) &&
      status === SeatLayoutStatus.WALKWAY_ON_THE_LEFT
    ) {
      // const updated = addCell(fields, fields[selectedSeatIndex], "left", "left");
      // setValue("seats", updated);

      const updated = fields;

      if (!updated[selectedSeatIndex]?.walkway?.includes(WALKWAY_VALUE.IN_LEFT)) {
        updated[selectedSeatIndex]?.walkway.push(WALKWAY_VALUE.IN_LEFT);
      }

      setValue("seats", updated);

      return;
    }

    if (
      selectedSeatIndex > -1 &&
      (position === SeatLayoutPosition.LEFT_TOP_DOWN || position === SeatLayoutPosition.LEFT_BOTTOM_UP) &&
      status === SeatLayoutStatus.WALKWAY_ON_THE_RIGHT
    ) {
      // const updated = addCell(fields, fields[selectedSeatIndex], "right", "right");
      // setValue("seats", updated);

      const updated = fields;

      if (!updated[selectedSeatIndex]?.walkway?.includes(WALKWAY_VALUE.IN_RIGHT)) {
        updated[selectedSeatIndex]?.walkway.push(WALKWAY_VALUE.IN_RIGHT);
      }

      setValue("seats", updated);

      return;
    }

    if (
      selectedSeatIndex > -1 &&
      (position === SeatLayoutPosition.RIGHT_TOP_DOWN || position === SeatLayoutPosition.RIGHT_BOTTOM_UP) &&
      status === SeatLayoutStatus.WALKWAY_ON_THE_LEFT
    ) {
      // const updated = addCell(fields, fields[selectedSeatIndex], "right", "right");
      // setValue("seats", updated);

      const updated = fields;

      if (!updated[selectedSeatIndex]?.walkway?.includes(WALKWAY_VALUE.IN_LEFT)) {
        updated[selectedSeatIndex]?.walkway.push(WALKWAY_VALUE.IN_LEFT);
      }

      setValue("seats", updated);

      return;
    }

    if (
      selectedSeatIndex > -1 &&
      (position === SeatLayoutPosition.RIGHT_TOP_DOWN || position === SeatLayoutPosition.RIGHT_BOTTOM_UP) &&
      status === SeatLayoutStatus.WALKWAY_ON_THE_RIGHT
    ) {
      // const updated = addCell(fields, fields[selectedSeatIndex], "left", "left");
      // setValue("seats", updated);

      const updated = fields;

      if (!updated[selectedSeatIndex]?.walkway?.includes(WALKWAY_VALUE.IN_RIGHT)) {
        updated[selectedSeatIndex]?.walkway.push(WALKWAY_VALUE.IN_RIGHT);
      }

      setValue("seats", updated);

      return;
    }

    if (
      selectedSeatIndex > -1 &&
      (position === SeatLayoutPosition.TOP_LEFT || position === SeatLayoutPosition.TOP_RIGHT) &&
      status === SeatLayoutStatus.WALKWAY_ABOVE
    ) {
      // const updated = addCell(fields, fields[selectedSeatIndex], "left", "left");
      // setValue("seats", updated);

      const updated = fields;

      if (!updated[selectedSeatIndex]?.walkway?.includes(WALKWAY_VALUE.IN_LEFT)) {
        updated[selectedSeatIndex]?.walkway.push(WALKWAY_VALUE.IN_LEFT);
      }

      setValue("seats", updated);

      return;
    }

    if (
      selectedSeatIndex > -1 &&
      (position === SeatLayoutPosition.TOP_LEFT || position === SeatLayoutPosition.TOP_RIGHT) &&
      status === SeatLayoutStatus.WALKWAY_BELOW
    ) {
      // const updated = addCell(fields, fields[selectedSeatIndex], "right", "right");
      // setValue("seats", updated);

      const updated = fields;

      if (!updated[selectedSeatIndex]?.walkway?.includes(WALKWAY_VALUE.IN_RIGHT)) {
        updated[selectedSeatIndex]?.walkway.push(WALKWAY_VALUE.IN_RIGHT);
      }

      setValue("seats", updated);

      return;
    }

    if (
      selectedSeatIndex > -1 &&
      (position === SeatLayoutPosition.BOTTOM_LEFT || position === SeatLayoutPosition.BOTTOM_RIGHT) &&
      status === SeatLayoutStatus.WALKWAY_ABOVE
    ) {
      // const updated = addCell(fields, fields[selectedSeatIndex], "right", "right");
      // setValue("seats", updated);

      const updated = fields;

      if (!updated[selectedSeatIndex]?.walkway?.includes(WALKWAY_VALUE.IN_LEFT)) {
        updated[selectedSeatIndex]?.walkway.push(WALKWAY_VALUE.IN_LEFT);
      }

      setValue("seats", updated);

      return;
    }

    if (
      selectedSeatIndex > -1 &&
      (position === SeatLayoutPosition.BOTTOM_LEFT || position === SeatLayoutPosition.BOTTOM_RIGHT) &&
      status === SeatLayoutStatus.WALKWAY_BELOW
    ) {
      // const updated = addCell(fields, fields[selectedSeatIndex], "left", "left");
      // setValue("seats", updated);

      const updated = fields;

      if (!updated[selectedSeatIndex]?.walkway?.includes(WALKWAY_VALUE.IN_RIGHT)) {
        updated[selectedSeatIndex]?.walkway.push(WALKWAY_VALUE.IN_RIGHT);
      }

      setValue("seats", updated);

      return;
    }

    if (selectedSeatIndex > -1) {
      setTimeout(() => {
        update(selectedSeatIndex, {
          ...watch("seats")[selectedSeatIndex],
          status: status
        })  
      }, 75)
    }
  };

  const handleResetWalkway = () => {
    if (postionSelected && fields?.length !== 0) {
      const resetWalkwaySeats = fields?.map(item => ({
        ...item,
        walkway: []
      }));

      setValue("seats", resetWalkwaySeats);
    }
  }

  const handleRemoveWalkway = (seatId: string, position: "left" | "right") => {
    console.log({ seatId, position });

    const selectedSeatIndex = fields.findIndex(seat => seat.id === seatId);

    const updated = fields;

    updated[selectedSeatIndex].walkway = updated[
      selectedSeatIndex
    ]?.walkway?.filter((value: string) => value !== `walkway_in_${position}`);

    setValue("seats", updated);
  }
  
  useEffect(() => {
    if (open && isEmpty(id)) {
      reset({
        row_name_type: "alphabet",
        row_name_position: "left_top_down",
        layout_name: "",
        number_of_columns: 1,
        number_of_rows: 1,
        seats: [],  
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id]);

  const _submit = (values: ISeatLayoutForm) => {
    if (!isGenerateNewLayout) {
      return toast.error("Please generate the latest layout before submitting")
    }
    onSubmit(values);
    onClose();
  };

  console.log(errors);

  if (isFetchingLayoutDetail) {
    return <FullScreenLoading />;
  }

  // =====> TOP HAY BOTTOM THÌ NGHIÊNG SANG PHẢI
  console.log("SEAT MAP:", fields);

  return (
    <Modal 
      title={`${!isEmpty(id) ? "Edit" : "Add"} seat layout`} 
      onClose={onClose} open={open}
      footer={(
        <div className="w-full">
          <div className="flex items-center justify-end gap-x-3">
            <Button
              className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
              onClick={onClose}
            >
              CANCEL
            </Button>
            <Button className="w-20" onClick={handleSubmit(_submit)}>
              {!isEmpty(id) ? "UPDATE" : "ADD"}
            </Button>
          </div>
        </div>
      )}
      closeOnClickBackdrop={false}
    >
      <div>
        <div className="w-full divide-y divide-gray-100 text-sm">
          <div className="box-container">
            <div className="grid sm:grid-cols-8 gap-y-4 gap-x-4 w-full mx-auto">
              <div className="col-span-full sm:col-span-2">
                <div className="flex flex-col items-start gap-x-4">
                  <Label name="Row name" id="row_name_type" hint="Configure the seat row name" required />
                  <Controller
                    control={control}
                    name="row_name_type"
                    render={({ field: { onChange, value = "", ref } }) => {
                      return (
                        <div className="flex flex-col gap-2">
                          {SEAT_ROW_NAME_TYPES.map(type => (
                            <RadioButton
                              // disabled={isOpenRegistration(get(eventDetailData, "open_registration_date", ""))}
                              checked={type.value === value}
                              onChange={() => {
                                onChange(type.value);
                              }}
                              item={{
                                id: type.value,
                                title: type.label,
                              }}
                              key={type.value}
                            />
                          ))}
                        </div>
                      );
                    }}
                  />
                  {get(errors, `row_name_type`, "") ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(errors, `row_name_type.message`, "")}
                      </span>
                    </p>
                  ) : null}
                </div>
              </div>

              {/* Role name position */}
              <div className="col-span-full sm:col-span-6">
                <div className="flex flex-col gap-x-4">
                  <Label name="Row name position" id="row_name_position" required />
                  <Controller
                    control={control}
                    name="row_name_position"
                    render={({ field, fieldState }) => {
                      return (
                        <div className="mt-1">
                          <Select
                            error={fieldState.error}
                            options={SEAT_POSITION}
                            id="row_name_position"
                            value={SEAT_POSITION.filter((item) =>
                              field.value.includes(item.value)
                            )}
                            placeholder="Select role name position"
                            onChange={(selected: {
                              label: string;
                              value: string;
                            }) => {
                              field.onChange(selected.value);
                              handleResetWalkway();
                            }}
                          />
                        </div>
                      );
                    }}
                  />
                  {get(errors, `row_name_position`, "") ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(errors, `row_name_position.message`, "")}
                      </span>
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="sm:col-span-2">
                <Label 
                  id="layout_name" 
                  name="Layout name"
                  required
                />
                <Input 
                  id="layout_name"
                  type="text"
                  placeholder="Layout name"
                  error={errors["layout_name"]}
                  {...register("layout_name")}
                />
                {errors["layout_name"] ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["layout_name"]?.message}
                    </span>
                  </p> 
                ): null}
              </div>

              <div className="sm:col-span-2">
                <Label
                  required
                  id="number_of_columns"
                  name="Number of columns"
                />
                <Input
                  id="number_of_columns"
                  type="number"
                  min={0}
                  placeholder="Number of columns"
                  {...register("number_of_columns", {
                    valueAsNumber: true,
                    onChange: () => {
                      setIsGenerateNewLayout(false);
                    }
                  })}
                  error={errors["number_of_columns"]}
                />
                {errors["number_of_columns"] ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["number_of_columns"]?.message}
                    </span>
                  </p> 
                ): null}
              </div>
              <div className="sm:col-span-2">
                <Label
                  required
                  id="number_of_rows"
                  name="Number of rows"
                />
                <Input
                  id="number_of_rows"
                  type="number"
                  min={0}
                  placeholder="Number of rows"
                  {...register("number_of_rows", {
                    valueAsNumber: true,
                    onChange: () => {
                      setIsGenerateNewLayout(false);
                    }
                  })}
                  error={errors["number_of_rows"]}
                />
                {errors["number_of_rows"] ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["number_of_rows"]?.message}
                    </span>
                  </p> 
                ): null}
              </div>
              <div className="sm:col-span-2">
                <div className="invisible">
                  <Label
                    required
                    id="refresh_button"
                    name="Refresh Button"
                  />
                </div>
                <Button className="w-full border border-solid border-brand-500" onClick={() => {
                  generateTableData(watch("number_of_rows"), watch("number_of_columns"));
                  setIsGenerateNewLayout(true);
                }} disabled={watch("number_of_rows") < 1 || watch("number_of_columns") < 1}>
                  Refresh new layout
                </Button>
              </div>
            </div>
            <div className="flex flex-col gap-y-4">
              <div className="w-full flex flex-wrap sm:justify-center sm:items-center gap-4 mt-6 mx-auto">
                {SEAT_STATUS.filter((type) => type.readOnly !== true).map(
                  (status) => {
                    if (
                      status.value === SeatLayoutStatus.WALKWAY_ON_THE_LEFT ||
                      status.value === SeatLayoutStatus.WALKWAY_ON_THE_RIGHT ||
                      status.value === SeatLayoutStatus.WALKWAY_ABOVE ||
                      status.value === SeatLayoutStatus.WALKWAY_BELOW
                    ) {
                      return null;
                    }

                    if (status.value === "hide") {
                      return (
                        <div
                          key={status.value}
                          className="flex items-center gap-x-2"
                        >
                          <div
                            className="relative flex items-center justify-center size-6 border border-solid font-medium rounded-full select-none
                                        border-[#CCD3FF] text-black bg-[#CCD3FF]"
                          ></div>
                          {status.label}
                        </div>
                      )
                    }

                    if (status.value === "walkway") {
                      return (
                        <div
                          key={status.value}
                          className="flex items-center gap-x-2"
                        >
                          <div
                            className="relative flex items-center justify-center size-6 border border-solid font-medium rounded-full select-none
                                        border-[#868E95] text-black bg-[#868E95]"
                          ></div>
                          {status.label}
                        </div>
                      )
                    }

                    return (
                      <div
                        key={status.value}
                        className="flex items-center gap-x-2"
                      >
                        <div
                          className={twMerge(
                            "relative flex items-center justify-center size-6 border border-solid font-medium rounded-full select-none",
                            status.value === SeatLayoutStatus.INACTIVE
                              ? "border-gray-300 text-black bg-gray-300"
                              : status.value === SeatLayoutStatus.SOLD_OUT
                              ? "border-brand-500 text-black bg-brand-500"
                              : "border-brand-500 text-brand-500 bg-white"
                          )}
                        >
                          {status.value === SeatLayoutStatus.UNAVAILABLE ? (
                            <XIcon className="size-4 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                          ) : (
                            ""
                          )}
                        </div>
                        {status.label}
                      </div>
                    );
                  }
                )}
              </div>
              <SeatmapTable isEdit={false} rowNameType={watch("row_name_type")} rowNamePosition={watch("row_name_position")} tableData={fields} _updateSeatStatus={_updateSeatStatus} handleRemoveWalkway={handleRemoveWalkway} />
              {errors["seats"] ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["seats"]?.message}
                    </span>
                  </p> 
                ): null}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
};
